/* Back Hero USA — cart drawer + /cart page behaviour (29/09/2026).
 * Owner: "Nhớ code đồng bộ cart cho đẹp như bên wakapaw nha" — the WakaPaw cart (wakapaw.com, assets/wk-cart.js parts 1 + 3):
 * number changes at once, a ring spins on the button just pressed, Check out rests until the cart has settled, one request per
 * change, never two cart writes in flight, only the last answer repaints the drawer.
 *
 * Who writes the cart:
 *   - assets/bh-gift-guard.js (agent B, loaded first): + / − / remove on PRODUCT lines that earn gifts (its window-capture click
 *     listener runs before this one and stops the event), the owner's gift rules #1–#7 after every change, the checkout gate.
 *   - this file: whatever the guard passes on — + / − / remove on gift lines and on products without gift rules (by line key),
 *     the size of a gift that has sizes (lumbar belt: new size added under the same product line, old size removed),
 *     "Clear all" (two steps), the opt-in Shipping Protection switch (and removing that line when no paid product is left),
 *     plus the loading state (html.bh-cart-wait) for EVERY cart change on the page (theme, buy box, guard, this file).
 *   Before each write it waits until the guard is idle and runs a check the guard still owes, so the two never race; after each
 *   write it hands the new cart to the guard (noteCart) so the guard's follow-up check needs no /cart.js read.
 * Rows need [data-key] + [data-variant-id] (+ Horizon's [data-parent-key] on child lines): snippets/cart-products.liquid.
 * Exposes window.BH.cart (debug: refresh()).
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const BH = (window.BH = window.BH || {});
if (!BH.cart) setup();

function setup() {
  const SOURCE = 'bh-cart';
  const PROTECT = 'lifetime-warranty-free-neck-pillow';
  const root = document.documentElement;
  const routes = () => window.Theme?.routes || {};
  const js = (u) => (u.endsWith('.js') ? u : `${u}.js`);
  const ADD = () => js(routes().cart_add_url || '/cart/add');
  const UPDATE = () => js(routes().cart_update_url || '/cart/update');
  const CART = () => js(routes().cart_url || '/cart');
  const CLEAR = () => `${String(routes().cart_url || '/cart').replace(/\.js$/, '')}/clear.js`;
  const guard = () => BH.giftGuard || null;

  const sectionIds = () => [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
  /** sections for a cart answer; withCart adds the guard's bh-product-cart section (the whole cart + parent keys) */
  const sections = (withCart) => {
    const g = guard();
    const ids = [...sectionIds(), ...(withCart && g?.cartSection ? [g.cartSection] : [])];
    return ids.length ? { sections: ids.join(','), sections_url: window.location.pathname } : {};
  };
  const drawerSections = (all) => {
    if (!all) return undefined;
    const out = { ...all };
    const g = guard();
    if (g?.cartSection) delete out[g.cartSection];
    return out;
  };

  async function post(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) {
      const error = new Error(data.description || data.message || `${url} ${res.status}`);
      error.status = res.status;
      throw error;
    }
    return data;
  }

  /* ---------------- what we know about the cart ---------------- */
  let mine = null;
  const latest = () => guard()?.lastCart?.() || mine;
  const note = (cart) => {
    if (!cart?.items) return;
    mine = cart;
    guard()?.noteCart?.(cart);
  };
  async function fetchCart() {
    const res = await fetch(CART(), { headers: { Accept: 'application/json' }, cache: 'no-store' });
    const cart = await res.json();
    note(cart);
    return cart;
  }
  const rowOf = (el) => el?.closest('tr[data-key]') || null;
  const parentVariantOf = (row) => {
    const pk = row.dataset.parentKey;
    if (!pk) return 0;
    const scope = row.closest('cart-items-component') || document;
    return Number(scope.querySelector(`tr[data-key="${CSS.escape(pk)}"]`)?.dataset.variantId) || 0;
  };
  /** a line's identity survives key changes: its variant + the variant of the product line it hangs under (0 = top level) */
  const idOf = (row) => `${row.dataset.variantId}:${parentVariantOf(row)}`;
  /** the key of that line in `cart` (the key on screen when the cart still has it) */
  function keyIn(cart, id, screenKey) {
    if (!cart?.items) return screenKey || null;
    if (screenKey && cart.items.some((i) => i.key === screenKey)) return screenKey;
    const [v, pv] = id.split(':').map(Number);
    const byKey = new Map(cart.items.map((i) => [i.key, i]));
    const hit = cart.items.find(
      (i) => i.variant_id === v && (pv ? byKey.get(i.parent_relationship?.parent_key)?.variant_id === pv : !i.parent_relationship)
    );
    return hit?.key || null;
  }

  /* ---------------- loading state for every cart change ---------------- */
  let seq = 0; // cart changes seen: an answer that arrives after a newer change must not repaint the drawer
  let holds = 0;
  let failsafe = 0;
  const paintWait = () => root.classList.toggle('bh-cart-wait', holds > 0);
  const unspin = () => {
    for (const el of document.querySelectorAll('[data-bh-spin]')) {
      el.classList.remove('bh-busy', 'bh-removing');
      el.removeAttribute('data-bh-spin');
    }
  };
  const hold = () => {
    holds++;
    paintWait();
    clearTimeout(failsafe);
    failsafe = setTimeout(() => {
      holds = 0; // never leave Check out resting on a lost promise
      paintWait();
      unspin();
    }, 12000);
  };
  const release = () => {
    holds = Math.max(0, holds - 1);
    paintWait();
    if (!holds) clearTimeout(failsafe);
  };
  const spin = (el, cls = 'bh-busy') => {
    if (!el) return;
    el.classList.add(cls);
    el.setAttribute('data-bh-spin', '');
  };

  /* Horizon decides once per open whether the footer sticks (footer ≤ half the drawer); measured before the repaint (e.g. while
     the drawer was still empty) it stayed "not sticky" and pushed Check out below the screen. Re-measure after each repaint
     through Horizon's own hook (cart-section:restored → cart-drawer-component re-measures on the next frame). */
  let restickTimers = [];
  const restick = () => {
    restickTimers.forEach(clearTimeout);
    restickTimers = [350, 1000].map((ms) =>
      setTimeout(() => document.querySelector('cart-drawer-component')?.dispatchEvent(new Event('cart-section:restored', { bubbles: true })), ms)
    );
  };
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    seq++;
    hold();
    const promise = event.promise ?? event.detail?.promise;
    Promise.resolve(promise)
      .then((r) => orphanCheck(r?.detail?.items), () => {})
      .finally(() => {
        release();
        restick();
      });
  });

  /** one cart write at a time from this file */
  let chain = Promise.resolve();
  function serial(task) {
    const run = chain.catch(() => {}).then(task);
    chain = run.catch(() => {});
    return run;
  }
  /** the guard first: nothing of its own on the wire, and the check it owes done now (not in the middle of our request) */
  async function settleGuard() {
    const g = guard();
    if (!g?.idle) return;
    await g.idle(8000);
    if (root.hasAttribute('data-bh-gift-pending') && !root.classList.contains('bh-cart-busy')) await g.check?.();
    await g.idle(8000);
  }
  /** the standard event Horizon repaints from (sections in the answer) and the guard follows */
  function announce(action, lines) {
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise }));
    const start = seq;
    return {
      done(cart, all) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source: SOURCE, sections: seq === start ? drawerSections(all) : undefined, didError: false },
        });
      },
      skip() {
        deferred.reject(new DOMException('Superseded by a newer cart change', 'AbortError'));
      },
      fail(error) {
        deferred.reject(error);
      },
    };
  }
  /** repaint from the server's cart (after an error) */
  function refresh() {
    const ev = announce('update', []);
    fetchCart().then(
      (cart) => ev.done(cart, undefined),
      (error) => ev.fail(error)
    );
  }

  /* ---------------- + / − / remove on the lines the guard passes on ---------------- */
  const want = new Map(); // line identity -> { q, key }
  let pumping = null;
  const buildUpdates = (cart, batch) => {
    const updates = {};
    for (const [id, w] of batch) {
      const key = keyIn(cart, id, w.key);
      if (key) updates[key] = w.q;
    }
    return updates;
  };
  function pump() {
    if (pumping) return pumping;
    pumping = serial(async () => {
      try {
        while (want.size) {
          await settleGuard();
          const batch = [...want.entries()];
          want.clear();
          let updates = buildUpdates(latest(), batch);
          if (!Object.keys(updates).length) continue;
          const ev = announce('update', Object.entries(updates).map(([id, quantity]) => ({ id, quantity })));
          let data;
          try {
            try {
              data = await post(UPDATE(), { updates, ...sections(false) });
            } catch (error) {
              if (error.status >= 500 || !error.status) throw error;
              updates = buildUpdates(await fetchCart(), batch); // a key went stale: once more with the server's keys
              data = await post(UPDATE(), { updates, ...sections(false) });
            }
          } catch (error) {
            ev.fail(error);
            throw error;
          }
          note(data);
          if (want.size) ev.skip(); // more clicks queued: only the last answer repaints
          else ev.done(data, data.sections);
        }
      } catch (error) {
        console.warn('[bh-cart] quantity', error);
        want.clear();
        refresh();
      } finally {
        pumping = null;
        unspin();
      }
    });
    return pumping;
  }

  window.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const button = target?.closest(
        'cart-items-component button[name="plus"], cart-items-component button[name="minus"], cart-items-component .cart-items__remove'
      );
      if (!button || button.disabled) return;
      const row = rowOf(button);
      if (!row?.dataset.variantId) return;
      const input = row.querySelector('input[name="updates[]"], input[type="number"]');
      const id = idOf(row);
      const kind = button.getAttribute('name') === 'plus' ? 1 : button.getAttribute('name') === 'minus' ? -1 : 0;
      const now = want.has(id) ? want.get(id).q : Number(input?.value) || 0;
      const min = Math.max(1, Number(input?.min) || 1);
      const max = input?.max ? Number(input.max) : Infinity;
      const q = kind === 0 ? 0 : kind > 0 ? Math.min(max, now + 1) : Math.max(min, now - 1);
      if (q === now) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      want.set(id, { q, key: row.dataset.key });
      if (input && q > 0) {
        input.value = String(q);
        row.querySelector('cart-quantity-selector-component')?.updateButtonStates?.();
      }
      if (q === 0) {
        spin(row, 'bh-removing');
        const scope = row.closest('cart-items-component') || document;
        for (const child of scope.querySelectorAll(`tr[data-parent-key="${CSS.escape(row.dataset.key)}"]`)) spin(child, 'bh-removing');
      }
      spin(button);
      hold();
      pump().finally(release);
    },
    true
  );

  /* ---------------- gift size (lumbar belt): new size under the same product line, old size out ---------------- */
  function swapSize(select) {
    const row = rowOf(select);
    if (!row) return;
    const from = Number(row.dataset.variantId);
    const to = Number(select.value);
    const pv = parentVariantOf(row);
    if (!from || !to || from === to || !pv) return;
    const screenKey = row.dataset.key;
    const parentKey = row.dataset.parentKey;
    const qty = Number(row.querySelector('input[name="updates[]"], input[type="number"]')?.value) || 1;
    spin(select.closest('.bh-line__size'));
    hold();
    serial(async () => {
      await settleGuard();
      let cart = latest() || (await fetchCart());
      const ev = announce('update', [
        { id: screenKey, quantity: 0 },
        { merchandiseId: String(to), quantity: qty },
      ]);
      try {
        // step 1 asks only for the cart data (fresh keys), not the drawer: the repaint comes with step 2
        const g = guard();
        const cartOnly = g?.cartSection ? { sections: g.cartSection, sections_url: window.location.pathname } : {};
        const addUnder = (key) => post(ADD(), { items: [{ id: to, quantity: qty, parent_line_key: key }], ...cartOnly });
        let answer;
        try {
          answer = await addUnder(keyIn(cart, `${pv}:0`, parentKey));
        } catch (error) {
          if (error.status >= 500 || !error.status) throw error;
          cart = await fetchCart();
          answer = await addUnder(keyIn(cart, `${pv}:0`, null));
        }
        const mid = guard()?.cartFromAnswer?.(answer) || (await fetchCart());
        const oldKey = keyIn(mid, `${from}:${pv}`, null);
        const data = await post(UPDATE(), { updates: oldKey ? { [oldKey]: 0 } : {}, ...sections(false) });
        note(data);
        ev.done(data, data.sections);
      } catch (error) {
        console.warn('[bh-cart] size', error);
        ev.fail(error);
        refresh();
      }
    }).finally(() => {
      release();
      unspin();
    });
  }

  /* ---------------- size / colour of ONE unit of a paid line (owner rule 01/10/2026) ----------------
     Shopify cannot change a line's variant, so one unit moves: the line loses 1, the chosen variant gains 1 (merging with a
     line of that variant when the cart has one). Prices, the Buy 2 / Buy 1 Get 1 discounts and the savings come back from the
     server (automatic discounts count every variant of the product, so the deal stays). The free gifts are child lines of the
     product line that holds them:
       - the line keeps ≥ 1 unit (or holds no gifts) → ONE /cart/update.js: { lineKey: qty − 1, newVariantId: qty there + 1 }
       - the line held the gifts and its only unit moves → /cart/add.js the new unit WITH the same gift lines (same variants,
         e.g. the belt size, parent_id = the new variant), then /cart/update.js the old line to 0 (Shopify drops its old gifts).
     Line properties (none on Back Hero today) ride along: such a line always takes the add path, properties copied.
     The guard sees one standard event and checks the final cart (gift count unchanged: same product, same units). */
  function swapUnit(select) {
    const row = rowOf(select);
    if (!row) return;
    // cart-like-chopchop 2026-10-23: a deal card lists the units of several lines; each select names its own line (data-bh-key / data-bh-from)
    const from = Number(select.dataset.bhFrom || row.dataset.variantId);
    const to = Number(select.value);
    if (!from || !to || from === to) return;
    const screenKey = select.dataset.bhKey || row.dataset.key;
    spin(select.closest('.bh-line__unit'));
    hold();
    const hasProps = (o) => !!o && typeof o === 'object' && Object.keys(o).length > 0;
    serial(async () => {
      await settleGuard();
      const find = (cart) =>
        cart?.items?.find((i) => i.key === screenKey) || cart?.items?.find((i) => i.variant_id === from && !i.parent_relationship) || null;
      let cart = latest();
      let line = find(cart);
      if (!line) {
        cart = await fetchCart();
        line = find(cart);
      }
      if (!line) throw new Error('line not in the cart any more');
      const kids = cart.items.filter((i) => i.parent_relationship?.parent_key === line.key);
      const target = cart.items.find((i) => i.variant_id === to && !i.parent_relationship);
      const moveKids = line.quantity <= 1 && kids.length > 0;
      const plain = !moveKids && !hasProps(line.properties) && !hasProps(target?.properties) && !line.selling_plan_allocation;
      const ev = announce('update', [
        { id: line.key, quantity: line.quantity - 1 },
        { merchandiseId: String(to), quantity: (target?.quantity || 0) + 1 },
      ]);
      try {
        let data;
        if (plain) {
          data = await post(UPDATE(), { updates: { [line.key]: line.quantity - 1, [to]: (target?.quantity || 0) + 1 }, ...sections(false) });
        } else {
          const items = [{ id: to, quantity: 1, ...(hasProps(line.properties) ? { properties: line.properties } : {}) }];
          if (moveKids)
            for (const k of kids)
              items.push({ id: k.variant_id, quantity: k.quantity, parent_id: to, ...(hasProps(k.properties) ? { properties: k.properties } : {}) });
          const g = guard();
          const cartOnly = g?.cartSection ? { sections: g.cartSection, sections_url: window.location.pathname } : {};
          const answer = await post(ADD(), { items, ...cartOnly });
          const mid = guard()?.cartFromAnswer?.(answer) || (await fetchCart());
          const old =
            mid.items.find((i) => i.key === line.key) ||
            mid.items.find((i) => i.variant_id === from && !i.parent_relationship && JSON.stringify(i.properties || {}) === JSON.stringify(line.properties || {}));
          data = await post(UPDATE(), { updates: old ? { [old.key]: line.quantity - 1 } : {}, ...sections(false) });
        }
        note(data);
        ev.done(data, data.sections);
      } catch (error) {
        ev.fail(error);
        throw error;
      }
    })
      .catch((error) => {
        console.warn('[bh-cart] unit variant', error);
        refresh();
      })
      .finally(() => {
        release();
        unspin();
      });
  }

  /* ---------------- upsell "+ Add" (snippets/bh-cart-upsell.liquid): 1 unit as a plain line, one request ---------------- */
  function addUpsell(button) {
    const variant = Number(button.dataset.bhUpsellAdd);
    if (!variant || button.disabled) return;
    button.disabled = true;
    spin(button);
    hold();
    serial(async () => {
      await settleGuard();
      const ev = announce('add', [{ merchandiseId: String(variant), quantity: 1 }]);
      try {
        const answer = await post(ADD(), { items: [{ id: variant, quantity: 1 }], ...sections(true) });
        const cart = guard()?.cartFromAnswer?.(answer) || (await fetchCart());
        note(cart);
        ev.done(cart, answer.sections);
      } catch (error) {
        ev.fail(error);
        throw error;
      }
    })
      .catch((error) => {
        console.warn('[bh-cart] upsell', error);
        button.disabled = false;
        refresh();
      })
      .finally(() => {
        release();
        unspin();
      });
  }
  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-bh-upsell-add]') : null;
    if (!button) return;
    event.preventDefault();
    addUpsell(button);
  });

  /* ---------------- deal card (cart-like-chopchop 2026-10-23, ported from ChopChop USA): SETS stepper + one trash for the whole group ----------------
     snippets/cart-products.liquid renders one card per deal (all paid lines of a product). Its stepper counts SETS (one set =
     the sizes of the first set, e.g. S + M): + = ONE /cart/add.js of one more set (the guard then scales the gifts by combo),
     − = ONE /cart/update.js removing the last set; the trash = ONE /cart/update.js setting every line of the group to 0
     (Shopify drops their gift lines). By variant id (keys change after edits). One CartLinesUpdateEvent each. */
  const specOf = (str) =>
    String(str || '')
      .split(';')
      .filter(Boolean)
      .map((p) => p.split(':').map(Number))
      .filter(([v, q]) => v > 0 && q >= 0);
  function groupWrite(button, kind) {
    const box = button.closest('[data-bh-sets]');
    const spec = kind === 'add' ? specOf(box?.dataset.bhSetAdd) : kind === 'minus' ? specOf(box?.dataset.bhSetMinus) : specOf(button.dataset.bhSetRemove);
    if (!spec.length || button.disabled) return;
    const row = rowOf(button);
    if (kind === 'remove' && row) {
      spin(row, 'bh-removing');
      const scope = row.closest('cart-items-component') || document;
      for (const v of spec.map(([id]) => id)) {
        for (const r of scope.querySelectorAll(`tr[data-variant-id="${v}"]`)) {
          spin(r, 'bh-removing');
          for (const k of scope.querySelectorAll(`tr[data-parent-key="${CSS.escape(r.dataset.key)}"]`)) spin(k, 'bh-removing');
        }
      }
    }
    button.disabled = true;
    spin(button);
    hold();
    serial(async () => {
      await settleGuard();
      const lines = spec.map(([id, quantity]) => ({ merchandiseId: String(id), quantity }));
      const ev = announce(kind === 'add' ? 'add' : kind === 'remove' ? 'remove' : 'update', lines);
      try {
        let cart;
        let all;
        if (kind === 'add') {
          const answer = await post(ADD(), { items: spec.map(([id, quantity]) => ({ id, quantity })), ...sections(true) });
          cart = guard()?.cartFromAnswer?.(answer) || (await fetchCart());
          all = answer.sections;
        } else {
          cart = await post(UPDATE(), { updates: Object.fromEntries(spec.map(([id, q]) => [id, q])), ...sections(false) });
          all = cart.sections;
        }
        note(cart);
        ev.done(cart, all);
      } catch (error) {
        ev.fail(error);
        throw error;
      }
    })
      .catch((error) => {
        console.warn('[bh-cart] deal set', error);
        button.disabled = false;
        refresh();
      })
      .finally(() => {
        release();
        unspin();
      });
  }
  document.addEventListener('click', (event) => {
    const t = event.target instanceof Element ? event.target : null;
    const step = t?.closest('[data-bh-set]');
    if (step) {
      event.preventDefault();
      return groupWrite(step, Number(step.dataset.bhSet) > 0 ? 'add' : 'minus');
    }
    const trash = t?.closest('[data-bh-group-remove]');
    if (trash) {
      event.preventDefault();
      groupWrite(trash, 'remove');
    }
  });

  /* ---------------- Shipping Protection (opt-in, one request) ---------------- */
  function toggleProtection(input) {
    const card = input.closest('[data-bh-protection-card]');
    const variant = Number(card?.dataset.variant);
    if (!variant) return;
    const on = input.checked;
    card.setAttribute('loading', '');
    hold();
    serial(async () => {
      await settleGuard();
      const ev = announce(on ? 'add' : 'update', [{ merchandiseId: String(variant), quantity: on ? 1 : 0 }]);
      try {
        let cart;
        let all;
        if (on) {
          const answer = await post(ADD(), { items: [{ id: variant, quantity: 1 }], ...sections(true) });
          cart = guard()?.cartFromAnswer?.(answer) || (await fetchCart());
          all = answer.sections;
        } else {
          cart = await post(UPDATE(), { updates: { [variant]: 0 }, ...sections(false) }); // one protection line: by variant id
          all = cart.sections;
        }
        note(cart);
        ev.done(cart, all);
      } catch (error) {
        console.warn('[bh-cart] protection', error);
        input.checked = !on;
        ev.fail(error);
        refresh();
      }
    }).finally(() => {
      card.removeAttribute('loading');
      release();
    });
  }
  /** no paid product left → the protection line goes too (it protects nothing) */
  let orphanBusy = false;
  function orphanCheck(items) {
    if (!Array.isArray(items) || orphanBusy) return;
    const line = items.find((i) => i.handle === PROTECT);
    if (!line || items.some((i) => i.handle !== PROTECT && i.final_line_price > 0)) return;
    orphanBusy = true;
    serial(async () => {
      await settleGuard();
      const now = latest();
      if (now?.items && (!now.items.some((i) => i.handle === PROTECT) || now.items.some((i) => i.handle !== PROTECT && i.final_line_price > 0))) return;
      const ev = announce('remove', [{ id: line.key, quantity: 0 }]);
      try {
        const cart = await post(UPDATE(), { updates: { [line.variant_id]: 0 }, ...sections(false) });
        note(cart);
        ev.done(cart, cart.sections);
      } catch (error) {
        ev.fail(error);
      }
    }).finally(() => {
      orphanBusy = false;
    });
  }

  document.addEventListener('change', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const select = target?.closest('select[data-bh-gift-size]');
    if (select) return swapSize(select);
    const unit = target?.closest('select[data-bh-unit]');
    if (unit) return swapUnit(unit);
    const toggle = target?.closest('input[data-bh-protection-toggle]');
    if (toggle) return toggleProtection(toggle);
  });

  /* ---------------- Clear all (first click arms, second clears, a click elsewhere disarms) ---------------- */
  let armed = null;
  const disarm = () => {
    if (!armed) return;
    armed.button.textContent = armed.label;
    armed.button.classList.remove('is-confirming');
    armed = null;
  };
  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-bh-clear-cart]') : null;
    if (!button) return disarm();
    event.preventDefault();
    if (!armed || armed.button !== button) {
      disarm();
      armed = { button, label: button.textContent.trim() || 'Clear all' };
      button.textContent = 'Confirm clear?';
      button.classList.add('is-confirming');
      return;
    }
    armed = null;
    button.disabled = true;
    hold();
    serial(async () => {
      await settleGuard();
      const ev = announce('remove', (latest()?.items || []).map((i) => ({ id: i.key, quantity: 0 })));
      try {
        const cart = await post(CLEAR(), { ...sections(false) });
        note(cart);
        ev.done(cart, cart.sections);
      } catch (error) {
        console.warn('[bh-cart] clear', error);
        ev.fail(error);
        button.disabled = false;
        button.textContent = 'Clear all';
        button.classList.remove('is-confirming');
        refresh();
      }
    }).finally(release);
  });

  /* ---------------- add to cart → the drawer opens on the NEW lines ----------------
     The buy box opens the drawer right after its add answer, before Horizon has repainted it (from an empty cart Horizon even
     animates the swap), so for a moment the drawer showed "Your cart is empty" / the old list. While an add is being painted,
     opening the cart drawer waits for that repaint (first DOM change in the drawer after the answer; 0.9–1.5 s failsafe). */
  let paintedAfterAdd = null;
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const drawer = document.querySelector('cart-drawer-component');
    const td = drawer?.closest('theme-drawer');
    const promise = event.promise ?? event.detail?.promise;
    if (!td || !promise || event.action !== 'add' || td.hasAttribute('open') || typeof td.open !== 'function') return;
    paintedAfterAdd = new Promise((resolve) => {
      let done = false;
      let timer = 0;
      const observer = new MutationObserver(() => finish());
      const finish = () => {
        if (done) return;
        done = true;
        observer.disconnect();
        clearTimeout(timer);
        requestAnimationFrame(() => resolve());
      };
      promise.then(
        (r) => {
          observer.observe(drawer, { childList: true, subtree: true, attributes: true, characterData: true });
          timer = setTimeout(finish, r?.detail?.sections ? 900 : 1500);
        },
        finish
      );
    }).finally(() => {
      paintedAfterAdd = null;
    });
    if (!td.__bhOpenAfterPaint) {
      td.__bhOpenAfterPaint = true;
      const open = td.open.bind(td);
      td.open = (...args) => (paintedAfterAdd ? paintedAfterAdd.then(() => open(...args)) : open(...args));
    }
  });

  /* ---------------- "You save" box: folded by default (owner rule 01/10/2026) ----------------
     A plain <details> rendered closed; folded again whenever the cart changes (any writer: the standard event) and whenever
     the drawer opens or closes, so "See details" never stays open from an earlier look. */
  const fold = () => {
    for (const d of document.querySelectorAll('cart-items-component details.bh-foot__save[open]')) d.open = false;
  };
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    fold();
    const promise = event.promise ?? event.detail?.promise;
    Promise.resolve(promise).finally(() => requestAnimationFrame(fold));
  });
  const drawerEl = document.querySelector('theme-drawer#cart-drawer');
  if (drawerEl) new MutationObserver(fold).observe(drawerEl, { attributes: true, attributeFilter: ['open'] });
  document.addEventListener('close', (event) => { if (event.target instanceof Element && event.target.closest('#cart-drawer')) fold(); }, true);

  // a cart left with only the protection line (older session): tidy it once the guard has read the cart
  setTimeout(() => orphanCheck(latest()?.items), 1500);

  BH.cart = { refresh, latest };
}
